import { type Context, createContext, useContext, useRef} from "react";
import { useStore } from "zustand";
import { 
    type StoreCreatorWithMiddlewares,
    createWithMiddlewares
} from './ZustandWithMiddlewares';

type Store<State> = ReturnType<typeof createWithMiddlewares<State>>;

type StoreContext<State> = Context<Store<State> | null> & {
    /** Provider に注入する store を生成する */
    createStore: () => Store<State>;
}

/**
 * Zustand を ReactContext 化するために必要な書式のユーティリティ
 * @see https://zustand.docs.pmnd.rs/guides/initialize-state-with-props
 */
export function createZustandContext<State extends object>(
  storeCreator: StoreCreatorWithMiddlewares<State>,
) {
  /**
   * Zustand Store を保有するコンテキスト
   * Provider を活用する際に createStore() で Store を生成することができる
   */
  const context = createContext<Store<State> | null>(null);
  const createStore = () => useRef(createWithMiddlewares(storeCreator)).current;
  Object.assign(context, { createStore });

  /**
   * Consumer が活用する useStore のテンプレート。
   * 基本的には、これに名前をつけて export して使えば OK。
   */
  function useZustandStore<T = State>(
    selector: (state: State) => T = (state) => state as unknown as T,
  ): T {
    const store = useContext(context);
    if (!store) throw new Error("Missing StoreContext.Provider in the tree");
    return useStore(store, selector);
  }

  return {
    context: context as StoreContext<State>,
    useStore: useZustandStore,
  };
}